import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight, Search } from "lucide-react";

const NAV = [
  { label: "About HARC", to: "/about" },
  { label: "Approach", to: "/approach" },
  { label: "Impact", to: "/impact" },
  { label: "Insights & Resources", to: "/insights" },
  { label: "Donate", to: "/donate" },
];

export const MockHeader = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submitSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    setSearchOpen(false);
    setOpen(false);
    navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setQ("");
  };

  return (
    <header
      className="sticky top-0 z-40 border-b border-[#004778]/25 bg-white/90 backdrop-blur-xl"
      data-testid="mockup-header"
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:bg-[#004778] focus:px-4 focus:py-2 focus:text-white"
      >
        Skip to content
      </a>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-3">
        <Link to="/" data-testid="mockup-logo" aria-label="HARC home">
          <img
            src="/assets/harc-logo.jpg"
            alt="HARC"
            className="h-10 w-auto"
          />
        </Link>
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV.map((n) => (
            <Link
              key={n.label}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="text-sm font-semibold text-[#022A42] transition-colors hover:text-[#004778]"
            >
              {n.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => setSearchOpen(!searchOpen)}
            aria-expanded={searchOpen}
            aria-label="Search the site"
            data-testid="header-search-toggle"
            className="text-[#022A42] transition-colors hover:text-[#004778]"
          >
            <Search size={19} />
          </button>
          <Link
            to="/start-a-conversation"
            data-testid="nav-start-conversation"
            className="inline-flex items-center gap-2 rounded-full bg-[#FFA100] px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#022A42] transition-colors hover:bg-[#D98A00]"
          >
            Start a Conversation <ArrowRight size={14} aria-hidden="true" />
          </Link>
        </nav>
        <div className="flex items-center gap-4 lg:hidden">
          <button
            type="button"
            onClick={() => setSearchOpen(!searchOpen)}
            aria-label="Search the site"
            data-testid="header-search-toggle-mobile"
            className="text-[#004778]"
          >
            <Search size={22} />
          </button>
          <button
            type="button"
            className="text-[#004778]"
            data-testid="mobile-menu-button"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <form
          onSubmit={submitSearch}
          role="search"
          className="border-t border-[#004778]/20 bg-white px-6 py-4"
          data-testid="header-search-form"
        >
          <div className="relative mx-auto max-w-3xl">
            <Search
              size={16}
              className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#5C6B72]"
              aria-hidden="true"
            />
            <input
              type="search"
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setSearchOpen(false)}
              placeholder="Search pages, articles, reports, podcasts…"
              aria-label="Search the site"
              data-testid="header-search-input"
              className="w-full border border-[#004778]/30 bg-white py-3 pl-10 pr-4 text-sm text-[#022A42] focus:outline-none focus:ring-2 focus:ring-[#004778]"
            />
          </div>
        </form>
      )}

      {open && (
        <nav
          className="border-t border-[#004778]/20 bg-white px-6 py-6 lg:hidden"
          aria-label="Mobile"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col gap-5">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                className="harc-heading text-xl text-[#004778]"
                onClick={() => setOpen(false)}
              >
                {n.label}
              </Link>
            ))}
            <Link
              to="/start-a-conversation"
              onClick={() => setOpen(false)}
              className="mt-2 inline-flex w-fit items-center gap-2 rounded-full bg-[#FFA100] px-6 py-3 text-xs font-bold uppercase tracking-[0.12em] text-[#022A42]"
            >
              Start a Conversation <ArrowRight size={14} aria-hidden="true" />
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
};
